import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { LogIn, Loader2 } from 'lucide-react';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const { login, isAuthenticated, user } = useAdminAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated — role-aware
  useEffect(() => {
    if (isAuthenticated && user) {
      const target = user.role === 'super_admin' ? '/admin/dashboard' : '/admin/activities';
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  /**
   * Validate form fields
   */
  const validate = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};

    // Email validation
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    // Password validation
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});

    // Validate form
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        toast.success('Login successful!');
        // Redirect handled by useEffect watching isAuthenticated + user.role
      } else {
        toast.error(result.error || 'Login failed');
        setErrors({ email: result.error });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-pine-paper flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Card */}
        <div className="bg-pine-card rounded-pine-card shadow-pine-card border border-pine-line p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-pine-l1 rounded-full mb-4">
              <LogIn className="w-8 h-8 text-pine-pine" />
            </div>
            <h1 className="text-heading-2 text-pine-ink mb-2">
              Play Moments Admin
            </h1>
            <p className="text-body-sm text-pine-ink-soft">
              Sign in to access the admin panel
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-body-sm font-medium text-pine-ink mb-2"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                className={`w-full px-4 py-3 bg-white border rounded-pine-input text-pine-ink placeholder:text-pine-draft focus:outline-none focus:ring-2 focus:ring-pine-pine transition-colors ${
                  errors.email
                    ? 'border-pine-danger'
                    : 'border-pine-line focus:border-pine-pine'
                }`}
                placeholder="sparkadmin@playmoments.app"
              />
              {errors.email && (
                <p className="mt-2 text-body-sm text-pine-danger">
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-body-sm font-medium text-pine-ink mb-2"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                className={`w-full px-4 py-3 bg-white border rounded-pine-input text-pine-ink placeholder:text-pine-draft focus:outline-none focus:ring-2 focus:ring-pine-pine transition-colors ${
                  errors.password
                    ? 'border-pine-danger'
                    : 'border-pine-line focus:border-pine-pine'
                }`}
                placeholder="Enter your password"
              />
              {errors.password && (
                <p className="mt-2 text-body-sm text-pine-danger">
                  {errors.password}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full admin-button-primary py-3 px-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
